import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Grid,
    Link as MuiLink,
    Paper,
    Snackbar,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { Link } from 'react-router-dom';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RedeemOutlinedIcon from '@mui/icons-material/RedeemOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers';
import CustomerService from '@api/Customers/CustomerCtrl';
import { es } from 'date-fns/locale/es';
import { PhoneInputField } from '@src/components/PhoneInputField';
import BrandLogo from '@src/components/BrandLogo';

const emailRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const phoneRegex = /^\+((52\d{10})|(1\d{10}))$/;

const customerSchema = yup.object({
    firstname: yup
        .string()
        .required('El nombre es requerido')
        .transform((value) => (value ? value.trim() : value)),
    lastname: yup
        .string()
        .required('El apellido es requerido')
        .transform((value) => (value ? value.trim() : value)),
    email: yup
        .string()
        .matches(emailRegex, 'Ingrese un email válido')
        .required('El email es requerido')
        .transform((value) => (value ? value.trim() : value)),
    phone: yup
        .string()
        .matches(phoneRegex, 'Ingrese un teléfono válido')
        .required('El teléfono es requerido')
        .transform((value) => (value ? value.trim() : value)),
    birthday: yup
        .date()
        .max(new Date(), 'La fecha no puede ser futura')
        .required('La fecha de nacimiento es requerida')
        .typeError('Ingrese una fecha válida')
});

type FormData = yup.InferType<typeof customerSchema>;

const BENEFITS = [
    {
        icon: <RedeemOutlinedIcon />,
        title: 'Pesos Kabandú',
        text: 'Acumulas en cada visita y los usas como pago cuando quieras.'
    },
    {
        icon: <ReceiptLongOutlinedIcon />,
        title: 'Tu ticket por WhatsApp',
        text: 'Recibes el detalle de tu servicio al terminar, sin papel.'
    },
    {
        icon: <PhoneIphoneOutlinedIcon />,
        title: 'Todo en la app',
        text: 'Consulta tu saldo, tus sucursales cercanas y las novedades.'
    }
];

const HomeScreen = () => {
    const [errorMessage, setErrorMessage] = useState('');
    const [registered, setRegistered] = useState(false);
    const [honeypot, setHoneypot] = useState('');
    const [loading, setLoading] = useState(false);
    const customerService = useMemo(() => new CustomerService(), []);

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
        setValue,
        reset
    } = useForm<FormData>({
        resolver: yupResolver(customerSchema),
        defaultValues: { phone: '+52' }
    });

    const onSubmit = async (data: FormData) => {
        if (honeypot) return;

        setLoading(true);
        try {
            await customerService.saveCustomer({ ...data, birthday: new Date(data.birthday) });
            reset({ phone: '+52' });
            setRegistered(true);
        } catch (error: any) {
            const raw = error?.message || '';
            // Los mensajes del servicio vienen en prosa; se traducen a algo accionable.
            const message = raw.includes('teléfono ya se encuentra registrado')
                ? 'Ese teléfono ya está registrado. Si ya eres clienta, pregunta en sucursal por tu cuenta.'
                : raw.includes('email ya se encuentra registrado')
                  ? 'Ese correo ya está registrado.'
                  : raw.includes('Formato de fecha inválido')
                    ? 'La fecha de nacimiento no es válida.'
                    : raw || 'No pudimos completar tu registro. Inténtalo de nuevo.';
            setErrorMessage(message);
        }
        setLoading(false);
    };

    const cleanInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setValue(name as keyof FormData, value.trim(), { shouldValidate: true });
    };

    if (registered) {
        return (
            <Stack spacing={3} alignItems="center" sx={{ maxWidth: 520, mx: 'auto', py: { xs: 4, sm: 8 } }}>
                <CheckCircleRoundedIcon sx={{ fontSize: 64, color: 'success.main' }} />
                <Typography variant="h4" textAlign="center">
                    ¡Listo, ya eres parte de Kabandú!
                </Typography>
                <Typography color="text.secondary" textAlign="center">
                    Tu cuenta quedó registrada. Descarga la app para ver tus Pesos Kabandú, encontrar tu sucursal más
                    cercana y enterarte de las novedades.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: '100%' }}>
                    <Button
                        component={Link}
                        to="/download"
                        variant="contained"
                        size="large"
                        fullWidth
                        startIcon={<PhoneIphoneOutlinedIcon />}
                    >
                        Descargar la app
                    </Button>
                    <Button variant="outlined" size="large" fullWidth onClick={() => setRegistered(false)}>
                        Registrar a alguien más
                    </Button>
                </Stack>
            </Stack>
        );
    }

    return (
        <>
            <Stack spacing={{ xs: 3, sm: 4 }} sx={{ maxWidth: 640, mx: 'auto' }}>
                {/* Encabezado de marca: antes la pantalla abría con un "Kabandu" en texto
                    plano dentro de la misma tarjeta del formulario. */}
                <Stack spacing={1.5} alignItems="center" sx={{ pt: { xs: 1, sm: 3 } }}>
                    <BrandLogo size={56} showName={false} justify="center" />
                    <Typography variant="overline" color="text.secondary">
                        REGISTRO DE CLIENTES
                    </Typography>
                    <Typography variant="h4" textAlign="center">
                        Crea tu cuenta Kabandú
                    </Typography>
                    <Typography color="text.secondary" textAlign="center" sx={{ maxWidth: 460 }}>
                        Se registra una sola vez. Con tu cuenta acumulas Pesos Kabandú en cada servicio y recibes tu
                        ticket por WhatsApp.
                    </Typography>
                </Stack>

                <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3.5 }, borderRadius: 3 }}>
                    <Stack component="form" onSubmit={handleSubmit(onSubmit)} noValidate spacing={2.5}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Nombre"
                                    fullWidth
                                    autoComplete="given-name"
                                    {...register('firstname')}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        register('firstname').onChange(e);
                                        cleanInput(e);
                                    }}
                                    error={!!errors.firstname}
                                    helperText={errors.firstname?.message}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Apellido"
                                    fullWidth
                                    autoComplete="family-name"
                                    {...register('lastname')}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        register('lastname').onChange(e);
                                        cleanInput(e);
                                    }}
                                    error={!!errors.lastname}
                                    helperText={errors.lastname?.message}
                                />
                            </Grid>
                        </Grid>

                        <TextField
                            label="Correo electrónico"
                            type="email"
                            fullWidth
                            autoComplete="email"
                            inputProps={{ inputMode: 'email' }}
                            {...register('email')}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                register('email').onChange(e);
                                cleanInput(e);
                            }}
                            error={!!errors.email}
                            helperText={errors.email?.message}
                        />

                        <PhoneInputField
                            value={watch('phone') || '+52'}
                            onChange={(value) => setValue('phone', value, { shouldValidate: true })}
                            error={!!errors.phone}
                            helperText={errors.phone?.message}
                            disabled={loading}
                        />

                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                            <DatePicker
                                label="Fecha de nacimiento"
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        error: !!errors.birthday,
                                        helperText: errors.birthday?.message || 'Para felicitarte en tu cumpleaños'
                                    }
                                }}
                                value={watch('birthday') || null}
                                onChange={(date: Date | null) => {
                                    if (date) setValue('birthday', date, { shouldValidate: true });
                                }}
                                maxDate={new Date()}
                                format="dd/MM/yyyy"
                            />
                        </LocalizationProvider>

                        {/* Trampa para bots: un campo oculto que una persona nunca llena. */}
                        <input
                            type="text"
                            name="website"
                            tabIndex={-1}
                            autoComplete="off"
                            style={{ display: 'none' }}
                            value={honeypot}
                            onChange={(e) => setHoneypot(e.target.value)}
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            size="large"
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : undefined}
                        >
                            {loading ? 'Registrando…' : 'Crear mi cuenta'}
                        </Button>

                        <Typography variant="caption" color="text.secondary" textAlign="center">
                            Al registrarte aceptas nuestros{' '}
                            <MuiLink component={Link} to="/terms" underline="hover" color="text.primary">
                                Términos
                            </MuiLink>{' '}
                            y el{' '}
                            <MuiLink component={Link} to="/privacy" underline="hover" color="text.primary">
                                Aviso de Privacidad
                            </MuiLink>
                            .
                        </Typography>
                    </Stack>
                </Paper>

                <Grid container spacing={2}>
                    {BENEFITS.map((benefit) => (
                        <Grid key={benefit.title} size={{ xs: 12, sm: 4 }}>
                            <Stack spacing={1} sx={{ height: '100%' }}>
                                <Box sx={{ color: 'text.primary' }}>{benefit.icon}</Box>
                                <Typography variant="subtitle2">{benefit.title}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {benefit.text}
                                </Typography>
                            </Stack>
                        </Grid>
                    ))}
                </Grid>
            </Stack>

            <Snackbar
                open={!!errorMessage}
                autoHideDuration={6000}
                onClose={() => setErrorMessage('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="error" variant="filled" onClose={() => setErrorMessage('')} sx={{ width: '100%' }}>
                    {errorMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default HomeScreen;
